/**
 * @file DeadlineContext.jsx
 * Contesto e logica di calcolo deadline per la pianificazione mensile.
 */

import { createContext, useCallback } from 'react';
/**
 * Deadline calculation context for planning deadlines.
 * Calculates and manages the deadline date for each month's planning submissions.
 * 
 * The deadline formula is: 4th working day (inclusive) before the last day of the PREVIOUS month, at 10:00 local time.
 * This is the date after which planning cells become locked for editing.
 * 
 * Provides:
 * - getDeadline(year, month): Returns calculated deadline date
 * - isCellLocked(year, month, date): Checks if a specific date is after the deadline
 * - isDeadlinePassed(year, month, savedDeadline?): Checks if deadline has passed
 */

export const DeadlineContext = createContext(null);

/**
 * Calcola la data limite per un mese/anno, con riferimento al mese PRECEDENTE
 * a quello selezionato: 4° giorno lavorativo (compreso) prima dell'ultimo giorno del mese precedente, alle 10:00.
 * Restituisce { dataLimite: 'YYYY-MM-DD', deadlineAt10: Date }
 */
/**
 * Torna indietro nel calendario fino al N-esimo giorno lavorativo (lun-ven), contando
 * come 1° giorno la data di partenza stessa se è lavorativa (conteggio inclusivo).
 */
function subtractWorkingDays(date, days) {
  const d = new Date(date);
  let remaining = days;
  while (remaining > 0) {
    const dow = d.getDay(); // 0=dom, 6=sab
    if (dow !== 0 && dow !== 6) remaining--;
    if (remaining === 0) break;
    d.setDate(d.getDate() - 1);
  }
  return d;
}

export function calcDeadline(year, month) {
  // Ultimo giorno del mese precedente a quello pianificato (gestisce anche il rollover di anno a gennaio)
  const lastDay = new Date(year, month - 1, 0);
  const result = subtractWorkingDays(lastDay, 4);
  const y = result.getFullYear();
  const m = String(result.getMonth() + 1).padStart(2, '0');
  const d = String(result.getDate()).padStart(2, '0');
  const dataLimite = `${y}-${m}-${d}`;
  // Scadenza effettiva: quel giorno alle 10:00 ora locale
  const deadlineAt10 = new Date(result.getFullYear(), result.getMonth(), result.getDate(), 10, 0, 0);
  return { dataLimite, deadlineAt10 };
}

/**
 * Cerca nei record PianificazioneStato la scadenza (epoch ms) per un dato anno/mese e stato.
 * @param {Array} records - array da dateLimitePianificazioni
 * @param {number} year
 * @param {number} month
 * @param {number} [stato=20] - codice stato da cercare (default 20 = Raccolta)
 * @returns {number|null} epoch ms oppure null se non trovato
 */
export function getDeadlineEpochFromRecords(records, year, month, stato = 20) {
  if (!Array.isArray(records) || !records.length) return null;
  const mStr = String(month).padStart(2, '0');
  const yStr = String(year);
  const rec = records.find(
    (r) => String(r.anno) === yStr && String(r.mese).padStart(2, '0') === mStr && r.stato === stato,
  );
  return rec?.scadenza ?? null;
}

export function DeadlineProvider({ children }) {
  /**
   * Restituisce la deadline calcolata per un mese/anno.
   * La formula è sempre: 4° giorno lavorativo (compreso) prima dell'ultimo giorno del mese precedente, alle 10:00.
   * Non usa valori salvati in DB (che potrebbero essere sporchi).
   */
  const getDeadline = useCallback((year, month) => {
    return calcDeadline(year, month);
  }, []);

  /**
   * Retrocompatibilità: true se la data passata è successiva alla deadline.
   */
  const isCellLocked = useCallback((year, month, date) => {
    const { deadlineAt10 } = calcDeadline(year, month);
    return new Date(date) > deadlineAt10;
  }, []);

  /**
   * Verifica se la deadline è scaduta.
   * @param {number} year
   * @param {number} month
   * @param {number|string|null} savedDeadline - epoch ms (number) oppure 'YYYY-MM-DD' (legacy) oppure null
   */
  const isDeadlinePassed = useCallback((year, month, savedDeadline = null) => {
    if (savedDeadline !== null && savedDeadline !== undefined) {
      if (typeof savedDeadline === 'number') {
        // nuovo: epoch ms dal servizio PianificazioneStato
        return new Date() > new Date(savedDeadline);
      }
      // legacy: stringa 'YYYY-MM-DD'
      const [y, m, d] = savedDeadline.split('-').map(Number);
      return new Date() > new Date(y, m - 1, d, 10, 0, 0);
    }
    const { deadlineAt10 } = calcDeadline(year, month);
    return new Date() > deadlineAt10;
  }, []);

  return (
    <DeadlineContext.Provider value={{ getDeadline, isCellLocked, calcDeadline, isDeadlinePassed }}>
      {children}
    </DeadlineContext.Provider>
  );
}
